import React, { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp, TrendingDown, PieChart, BarChart3, Plus,
  Wallet, ArrowUpRight, ArrowDownRight, RefreshCw, Trash2, Home, Landmark, Activity,
  Gem, Building2, Coins, Briefcase, FileText, CheckCircle2, AlertCircle, Info, Sparkles, Bot, Cpu, LineChart, MoveUpRight, Infinity, X, Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import DashboardLayout from '../components/DashboardLayout';
import { financialDataAPI, portfolioAPI } from '../services/apiServices';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, Cell
} from 'recharts';
import { toast } from 'sonner';

// === 3D TILT CARD COMPONENT ===
const TiltCard = ({ children, className, depth = 5, glowColor = "rgba(139, 92, 246, 0.1)" }) => {
  const cardRef = React.useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -depth;
    const rotateY = ((x - centerX) / centerX) * depth;

    card.style.setProperty('--mouse-x', `${x}px`);
    card.style.setProperty('--mouse-y', `${y}px`);
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg)`;
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`relative transition-all duration-300 ease-out preserve-3d group ${className}`}
      style={{
        backgroundImage: `radial-gradient(400px circle at var(--mouse-x) var(--mouse-y), ${glowColor}, transparent 40%)`
      }}
    >
      {children}
    </div>
  );
};

const Portfolio = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [chartRange, setChartRange] = useState(15); // Default to 15 days

  // === PROFESSIONAL ASSET SEGREGATION ===
  // Liquid Assets: Real-time tradable securities (Stocks, Mutual Funds, Crypto)
  const liquidAssets = useMemo(() =>
    assets.filter(a => ['Stocks', 'Mutual Fund', 'Crypto'].includes(a.assetType)),
    [assets]
  );

  // Fixed Assets: Non-liquid holdings (FD, Real Estate, Land, etc.)
  const fixedAssets = useMemo(() =>
    assets.filter(a => !['Stocks', 'Mutual Fund', 'Crypto'].includes(a.assetType)),
    [assets]
  );

  // === 4 KEY FINANCIAL METRICS ===
  // 1. Portfolio Value (Liquid Assets - Real-time) → Syncs to Dashboard
  const portfolioValue = useMemo(() =>
    liquidAssets.reduce((sum, a) => sum + (a.currentValue || 0), 0),
    [liquidAssets]
  );

  const portfolioInvested = useMemo(() =>
    liquidAssets.reduce((sum, a) => sum + (a.totalInvested || 0), 0),
    [liquidAssets]
  );

  const portfolioPnL = useMemo(() => portfolioValue - portfolioInvested, [portfolioValue, portfolioInvested]);
  const portfolioPnLPercent = useMemo(() =>
    portfolioInvested > 0 ? (portfolioPnL / portfolioInvested) * 100 : 0,
    [portfolioPnL, portfolioInvested]
  );

  // 2. Fixed Assets Value (Static sum)
  const fixedAssetsValue = useMemo(() =>
    fixedAssets.reduce((sum, a) => sum + (a.currentValue || 0), 0),
    [fixedAssets]
  );

  const fixedAssetsInvested = useMemo(() =>
    fixedAssets.reduce((sum, a) => sum + (a.totalInvested || 0), 0),
    [fixedAssets]
  );

  // 3. Net Worth (Total = Portfolio + Fixed Assets)
  const netWorth = useMemo(() => portfolioValue + fixedAssetsValue, [portfolioValue, fixedAssetsValue]);

  // 4. Total Invested Capital (Starting amount - Fixed once entered)
  const investedCapital = useMemo(() => portfolioInvested + fixedAssetsInvested, [portfolioInvested, fixedAssetsInvested]);

  // Overall P&L
  const totalPnL = useMemo(() => netWorth - investedCapital, [netWorth, investedCapital]);
  const totalPnLPercent = useMemo(() =>
    investedCapital > 0 ? (totalPnL / investedCapital) * 100 : 0,
    [totalPnL, investedCapital]
  );

  // Visual Data (Based on Portfolio Value for Dashboard Sync)
  const chartData = useMemo(() => {
    return Array.from({ length: chartRange }, (_, i) => ({
      name: `Day ${i}`,
      value: portfolioInvested + (portfolioPnL * (i / chartRange)) + (Math.random() * portfolioInvested * 0.01)
    }));
  }, [portfolioInvested, portfolioPnL, chartRange]);

  // --- Daily Snapshot Logic ---
  const checkAndSnapshot = async (currentTotal, currentInvested) => {
    if (currentTotal === 0) return; // Don't snapshot empty

    const today = new Date().toISOString().split('T')[0];
    const lastSnapshotDate = localStorage.getItem('lastPortfolioSnapshotDate');

    // Simple check: Only snapshot once per day per browser session context
    // Ideally this is done by backend, but frontend trigger is acceptable for MVP
    if (lastSnapshotDate !== today) {
      console.log("Taking daily portfolio snapshot...");
      try {
        await portfolioAPI.recordHistory({
          date: today,
          total_value: currentTotal,
          invested_value: currentInvested
        });
        localStorage.setItem('lastPortfolioSnapshotDate', today);
      } catch (e) {
        console.error("Snapshot failed:", e);
      }
    }
  };

  // --- Fetch Initial Data from Supabase ---
  const fetchPortfolio = async () => {
    try {
      const data = await portfolioAPI.getPortfolio();
      if (data) {
        const mappedAssets = data.map(asset => {
          const quantity = Number(asset.quantity);
          const buyPrice = Number(asset.buy_price);
          const currentPrice = Number(asset.current_price || 0);
          const totalInvested = quantity * buyPrice;
          const currentValue = quantity * currentPrice;
          const profitLoss = currentValue - totalInvested;
          const profitLossPercent = totalInvested > 0 ? (profitLoss / totalInvested) * 100 : 0;

          return {
            ...asset,
            quantity,
            buyPrice,
            currentPrice,
            totalInvested,
            currentValue,
            profitLoss,
            profitLossPercent,
            interestRate: asset.interest_rate ? Number(asset.interest_rate) : null,
            buyDate: asset.buy_date,
            assetType: asset.asset_type,
            assetName: asset.asset_name,
            ticker: asset.ticker,
            description: asset.description
          };
        });
        setAssets(mappedAssets);

        // Snapshot Check (after state set)
        const calcTotal = mappedAssets.reduce((sum, a) => sum + (a.currentValue || 0), 0);
        const calcInvested = mappedAssets.reduce((sum, a) => sum + (a.totalInvested || 0), 0);
        checkAndSnapshot(calcTotal, calcInvested);

        // APY / Caching logic
        const lastUpdateStr = sessionStorage.getItem('lastPortfolioUpdate');
        const now = Date.now();
        const FIVE_MINUTES = 5 * 60 * 1000;

        if (!lastUpdateStr || (now - Number(lastUpdateStr) > FIVE_MINUTES)) {
          // First entry in session or stale data - update market data
          console.log("[Portfolio] Data stale or first-session, syncing live prices...");
          updateMarketData(mappedAssets);
        } else {
          // Relies on DB prices (cached from previous update)
          setLastUpdated(new Date(Number(lastUpdateStr)));
        }
      }
    } catch (error) {
      console.error("Error fetching portfolio:", error);
      toast.error("Could not load portfolio.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Real-time Logic ---
  const updateMarketData = async (currentAssets) => {
    setUpdating(true);
    try {
      let updatedAssets = [...currentAssets];
      let changesMade = false;
      const dbUpdates = []; // Collect updates for background save

      // 1. Fixed Deposits (Compound Interest - Quarterly Compounding Standard)
      updatedAssets = updatedAssets.map(asset => {
        if (asset.assetType === 'Fixed Deposit' && asset.interestRate) {
          const startDate = new Date(asset.buyDate);
          const now = new Date();
          const diffTime = Math.abs(now - startDate);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          const years = diffDays / 365.25;

          // Formula: A = P * (1 + r/n)^(nt)
          // r = rate/100, n = 4 (Quarterly is standard for banks)
          const P = asset.totalInvested;
          const r = asset.interestRate / 100;
          const n = 4;

          const amount = P * Math.pow((1 + (r / n)), (n * years));
          const interest = amount - P;

          // Only update if there's a significant change or if it's currently 0
          if (Math.abs(asset.currentValue - amount) > 0.01 || asset.currentValue === 0) {
            changesMade = true;
            const newCurrentValue = parseFloat(amount.toFixed(2));
            const newProfitLoss = parseFloat(interest.toFixed(2));
            const newProfitLossPercent = P > 0 ? parseFloat(((interest / P) * 100).toFixed(2)) : 0;

            dbUpdates.push({ id: asset.id, current_price: newCurrentValue });

            return {
              ...asset,
              currentValue: newCurrentValue,
              currentPrice: newCurrentValue,
              profitLoss: newProfitLoss,
              profitLossPercent: newProfitLossPercent
            };
          }
        }
        return asset;
      });

      // 2. Stocks - Fetch Live Price
      const stocks = updatedAssets.filter(a => a.assetType === 'Stocks' && a.ticker);

      // 3. Mutual Funds - Fetch NAV
      const mfs = updatedAssets.filter(a => a.assetType === 'Mutual Fund' && a.ticker);

      // 4. Crypto - Fetch Price
      const cryptos = updatedAssets.filter(a => a.assetType === 'Crypto' && a.ticker);

      const stockPromises = stocks.map(async (stock) => {
        try {
          const marketData = await financialDataAPI.getStockPrice(stock.ticker);
          if (marketData && marketData.price) {
            const newPrice = Number(marketData.price);
            dbUpdates.push({ id: stock.id, current_price: newPrice });
            const idx = updatedAssets.findIndex(a => a.id === stock.id);
            if (idx !== -1) {
              updatedAssets[idx].currentPrice = newPrice;
              updatedAssets[idx].currentValue = newPrice * updatedAssets[idx].quantity;
              updatedAssets[idx].profitLoss = updatedAssets[idx].currentValue - updatedAssets[idx].totalInvested;
              updatedAssets[idx].profitLossPercent = (updatedAssets[idx].profitLoss / updatedAssets[idx].totalInvested) * 100;
              changesMade = true;
            }
          }
        } catch (e) { console.warn(`Failed to update stock ${stock.ticker}`, e); }
      });

      const mfPromises = mfs.map(async (mf) => {
        try {
          const navData = await financialDataAPI.getMFNAV(mf.ticker);
          if (navData && navData.price) {
            const newNAV = Number(navData.price);
            dbUpdates.push({ id: mf.id, current_price: newNAV });
            const idx = updatedAssets.findIndex(a => a.id === mf.id);
            if (idx !== -1) {
              updatedAssets[idx].currentPrice = newNAV;
              updatedAssets[idx].currentValue = newNAV * updatedAssets[idx].quantity;
              updatedAssets[idx].profitLoss = updatedAssets[idx].currentValue - updatedAssets[idx].totalInvested;
              updatedAssets[idx].profitLossPercent = (updatedAssets[idx].profitLoss / updatedAssets[idx].totalInvested) * 100;
              changesMade = true;
            }
          }
        } catch (e) { console.warn(`Failed to update MF ${mf.ticker}`, e); }
      });

      const cryptoPromises = cryptos.map(async (crypto) => {
        try {
          const cryptoData = await financialDataAPI.getCryptoPrice(crypto.ticker);
          if (cryptoData && cryptoData.price) {
            const newPrice = Number(cryptoData.price);
            dbUpdates.push({ id: crypto.id, current_price: newPrice });
            const idx = updatedAssets.findIndex(a => a.id === crypto.id);
            if (idx !== -1) {
              updatedAssets[idx].currentPrice = newPrice;
              updatedAssets[idx].currentValue = newPrice * updatedAssets[idx].quantity;
              updatedAssets[idx].profitLoss = updatedAssets[idx].currentValue - updatedAssets[idx].totalInvested;
              updatedAssets[idx].profitLossPercent = (updatedAssets[idx].profitLoss / updatedAssets[idx].totalInvested) * 100;
              changesMade = true;
            }
          }
        } catch (e) { console.warn(`Failed to update crypto ${crypto.ticker}`, e); }
      });

      await Promise.all([...stockPromises, ...mfPromises, ...cryptoPromises]);

      if (changesMade) {
        setAssets(updatedAssets);
        // Save snapshot immediately or defer?
        // Let's defer DB updates to avoid blocking UI or rapid writes
        if (dbUpdates.length > 0) {
          portfolioAPI.updatePricesBulk(dbUpdates).catch(e => console.warn("Background update failed", e));
        }

        sessionStorage.setItem('lastPortfolioUpdate', Date.now().toString());
      }
    } catch (err) {
      console.error("Error updating market data:", err);
      toast.error("Live market updates failed partially.");
    } finally {
      setUpdating(false);
    }
  };



  // --- Handlers ---
  const handleAddAsset = async (newAsset) => {
    try {
      console.log("Adding asset:", newAsset);

      // Ensure defaults for optional fields to satisfy DB constraints
      const dbPayload = {
        asset_type: newAsset.assetType,
        asset_name: newAsset.assetName,
        ticker: newAsset.ticker || null,
        quantity: newAsset.quantity || 1,
        buy_price: newAsset.buyPrice || 0,
        current_price: newAsset.currentPrice || newAsset.buyPrice || 0,
        buy_date: newAsset.buyDate || new Date().toISOString().split('T')[0],
        interest_rate: newAsset.interestRate || null,
        maturity_date: newAsset.maturityDate || null,
        description: newAsset.description || null,
      };

      const savedAsset = await portfolioAPI.addAsset(dbPayload);

      // The API should return the saved object. If not, we fallback to a random ID for UI (though this indicates DB failure)
      if (!savedAsset && !savedAsset.id) throw new Error("Database save failed");

      const currentValue = newAsset.currentPrice * newAsset.quantity;
      const profitLoss = currentValue - newAsset.totalInvested;
      const profitLossPercent = newAsset.totalInvested > 0 ? (profitLoss / newAsset.totalInvested) * 100 : 0;

      const uiAsset = {
        ...newAsset,
        id: savedAsset.id,
        currentValue,
        profitLoss,
        profitLossPercent
      };
      setAssets(prev => [uiAsset, ...prev]);

      // Re-trigger snapshot check (in case this was the first asset)
      // And maybe trigger price fetch if it's a new stock
      if (['Stocks', 'Mutual Fund', 'Crypto'].includes(newAsset.assetType)) {
        // Quick single update attempt for the new asset
        // (Or just let the user click refresh. For now let's just add it.)
      }

      const newTotal = totalValue + uiAsset.currentValue;
      const newInvested = totalInvested + uiAsset.totalInvested;
      checkAndSnapshot(newTotal, newInvested);

      toast.success("Asset saved securely!");
    } catch (e) {
      console.error("Save Asset Error:", e);
      toast.error("Could not save asset to database.");
    }
  };

  const handleRemoveAsset = async (id) => {
    if (!window.confirm("Delete this asset matrix entry?")) return;

    // Optimistic Update: Remove instantly from UI
    const previousAssets = [...assets];
    setAssets(prev => prev.filter(a => a.id !== id));

    try {
      await portfolioAPI.deleteAsset(id);
      toast.success("Terminal Entry Purged.");
    } catch (e) {
      // Revert if failed
      setAssets(previousAssets);
      toast.error("Neural bypass failed. Entry retained.");
    }
  };

  const allocationData = useMemo(() => {
    const groups = assets.reduce((acc, curr) => {
      const type = curr.assetType || 'Other';
      acc[type] = (acc[type] || 0) + curr.currentValue;
      return acc;
    }, {});
    return Object.keys(groups).map(key => ({ name: key, value: groups[key] }));
  }, [assets]);

  const COLORS = ['#3B82F6', '#8B5CF6', '#22D3EE', '#10B981', '#F43F5E', '#F59E0B'];

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#020617] p-4 md:p-8 font-sans selection:bg-indigo-500/30 overflow-x-hidden">

        {/* === CUSTOM PREMIUM STYLES === */}
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
          .font-sans { font-family: 'Plus Jakarta Sans', sans-serif !important; }
          
          .glass-card {
            background: #000;
            backdrop-filter: blur(25px);
            border: 1px solid rgba(139, 92, 246, 0.1);
            box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.7);
          }

          .premium-gradient-1 { background: linear-gradient(135deg, rgba(20, 25, 50, 0.9), rgba(5, 5, 15, 1)); }
          
          .text-shimmer {
            background: linear-gradient(90deg, #fff, rgba(139, 92, 246, 0.8), #6366f1, #fff);
            background-size: 200% auto;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: shimmer 4s linear infinite;
          }

          .hover-3d {
            transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.6s ease;
            transform-style: preserve-3d;
            perspective: 1000px;
          }
          .hover-3d:hover {
            transform: translateY(-8px) rotateX(4deg) rotateY(4deg);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 20px rgba(99, 102, 241, 0.1);
          }

          @keyframes shimmer {
            to { background-position: 200% center; }
          }

          .glow-blue { box-shadow: 0 0 40px rgba(59, 130, 246, 0.05); }
          .glow-purple { box-shadow: 0 0 40px rgba(139, 92, 246, 0.05); }

          .custom-scrollbar::-webkit-scrollbar { width: 3px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(139, 92, 246, 0.15); border-radius: 20px; }
        `}</style>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
          <div className="animate-in fade-in slide-in-from-left duration-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-2 px-2 py-0.5 bg-indigo-500/10 rounded-full border border-indigo-500/20">
                <Cpu size={10} className="text-indigo-400" />
                <span className="text-[8px] font-black uppercase tracking-[0.3em] text-indigo-400">{loading ? "Syncing..." : "Live Connection"}</span>
              </div>
              <p className="text-slate-500 text-[9px] font-bold uppercase tracking-wider opacity-60">
                Synced: {lastUpdated.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}
              </p>
            </div>
            <h1 className="text-xl md:text-3xl font-black text-white tracking-tighter uppercase leading-none">
              PORTFOLIO <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">MANAGEMENT</span>
            </h1>
          </div>
          <div className="flex flex-wrap gap-3 items-center w-full md:w-auto">
            <button
              onClick={() => updateMarketData(assets)}
              disabled={updating}
              className={`group flex flex-1 md:flex-none items-center justify-center space-x-2 bg-black border border-white/10 text-white px-4 py-2 rounded-xl font-bold transition-all duration-300 ${updating ? 'opacity-70' : 'hover:bg-white/5'}`}>
              <RefreshCw size={16} className={`text-indigo-400 ${updating ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-700"}`} />
              <span className="text-xs tracking-tight uppercase">{updating ? "Working..." : "Refresh"}</span>
            </button>
            <div className="h-8 w-[1px] bg-white/5 hidden md:block mx-1"></div>
            <AddAssetButton
              onAdd={handleAddAsset}
              assetType="liquid"
              label="Add Liquid"
              icon={<Sparkles size={14} />}
              className="flex-1 md:flex-none bg-indigo-600/20 text-indigo-400 border border-indigo-500/30 hover:bg-indigo-600/30 text-[10px] md:text-xs px-3 md:px-5 py-2"
            />
            <AddAssetButton
              onAdd={handleAddAsset}
              assetType="fixed"
              label="Add Fixed"
              icon={<Building2 size={14} />}
              className="flex-1 md:flex-none bg-purple-600/20 text-purple-400 border border-purple-500/30 hover:bg-purple-600/30 text-[10px] md:text-xs px-3 md:px-5 py-2"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* 1. NET WORTH - Black Terminal */}
          <TiltCard className="group relative overflow-hidden rounded-[1.5rem] md:rounded-[2rem] p-4 md:p-6 bg-black border border-purple-500/30 shadow-[0_0_15px_rgba(168,85,247,0.05)] hover:border-purple-500/60 transition-all duration-300">
            <div className="absolute -top-1 -right-1 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <Wallet size={55} className="text-purple-400" />
            </div>
            <h3 className="text-slate-400 text-[11px] font-black uppercase tracking-[0.2em] mb-2">Net Worth</h3>
            <div className="relative z-10">
              <h2 className="text-xl font-black tracking-tight text-white mb-1 group-hover:text-shimmer transition-all">
                ₹{netWorth.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </h2>
              <div className="flex items-center gap-1.5">
                <div className={`flex items-center text-[9px] font-black tracking-tighter ${totalPnL >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {totalPnL >= 0 ? <ArrowUpRight size={8} className="mr-0.5" /> : <ArrowDownRight size={8} className="mr-0.5" />}
                  {totalPnLPercent.toFixed(2)}%
                </div>
                <span className="text-[11px] text-slate-400 font-bold uppercase tracking-widest">Global Sync</span>
              </div>
            </div>
          </TiltCard>

          {/* 2. PORTFOLIO VALUE/WORTH - Black Terminal */}
          <TiltCard className="group relative overflow-hidden rounded-[2rem] p-6 bg-black border border-purple-500/30 shadow-[0_0_15px_rgba(168,85,247,0.05)] hover:border-purple-500/60 transition-all duration-300">
            <div className="absolute -top-1 -right-1 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <Activity size={55} className="text-purple-400" />
            </div>
            <h3 className="text-purple-300 text-[11px] font-black uppercase tracking-[0.2em] mb-2">Portfolio Value/Worth</h3>
            <div className="relative z-10">
              <h2 className="text-xl font-black tracking-tight text-white mb-1 group-hover:text-shimmer transition-all">
                ₹{portfolioValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </h2>
              <div className="flex items-center gap-1.5">
                <div className={`text-[9px] font-black tracking-tighter ${portfolioPnL >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {portfolioPnL >= 0 ? '+' : '-'}₹{Math.abs(portfolioPnL).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </div>
                <span className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-black rounded-md flex items-center gap-0.5">
                  <span className="w-0.5 h-0.5 bg-emerald-400 rounded-full animate-pulse"></span>
                  LIVE
                </span>
              </div>
            </div>
          </TiltCard>

          {/* 3. NON-LIQUID VALUE - Black Terminal */}
          <TiltCard className="group relative overflow-hidden rounded-[2rem] p-6 bg-black border border-purple-500/30 shadow-[0_0_15px_rgba(168,85,247,0.05)] hover:border-purple-500/60 transition-all duration-300">
            <div className="absolute -top-1 -right-1 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <Building2 size={55} className="text-purple-400" />
            </div>
            <h3 className="text-purple-300 text-[11px] font-black uppercase tracking-[0.2em] mb-2">Non-Liquid Value</h3>
            <div className="relative z-10">
              <h2 className="text-xl font-black tracking-tight text-white mb-1 group-hover:text-shimmer transition-all">
                ₹{fixedAssetsValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </h2>
              <div className="text-[9px] text-purple-400 font-black tracking-widest uppercase">{fixedAssets.length} Holdings</div>
            </div>
          </TiltCard>

          {/* 4. INVESTED CAPITAL - Black Terminal */}
          <TiltCard className="group relative overflow-hidden rounded-[2rem] p-6 bg-black border border-purple-500/30 shadow-[0_0_15px_rgba(168,85,247,0.05)] hover:border-purple-500/60 transition-all duration-300">
            <div className="absolute -top-1 -right-1 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <PieChart size={55} className="text-purple-400" />
            </div>
            <h3 className="text-purple-300 text-[11px] font-black uppercase tracking-[0.2em] mb-2">Invested Capital</h3>
            <div className="relative z-10">
              <h2 className="text-xl font-black tracking-tight text-white mb-1 group-hover:text-shimmer transition-all">
                ₹{investedCapital.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </h2>
              <div className="text-[9px] text-purple-600 font-black tracking-widest uppercase">Cap. Base Line</div>
            </div>
          </TiltCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* PERFORMANCE LINE CHART */}
          <TiltCard className="lg:col-span-2 bg-black rounded-[2.5rem] p-8 border border-purple-500/30 relative overflow-hidden group shadow-[0_0_15px_rgba(168,85,247,0.05)] transition-all duration-500">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-white text-base font-black uppercase tracking-tight">Portfolio Performance</h3>
                <p className="text-slate-400 text-[11px] font-bold uppercase tracking-widest mt-1">Growth Trajectory</p>
              </div>
              <div className="flex items-center gap-2 bg-black p-1 rounded-xl border border-white/10">
                <button
                  onClick={() => setChartRange(15)}
                  className={`px-3 py-1 text-[10px] font-black transition-all ${chartRange === 15 ? 'text-indigo-400 bg-indigo-500/10 rounded-lg shadow-inner' : 'text-slate-500 hover:text-white'}`}>
                  15D
                </button>
                <button
                  onClick={() => setChartRange(30)}
                  className={`px-3 py-1 text-[10px] font-black transition-all ${chartRange === 30 ? 'text-indigo-400 bg-indigo-500/10 rounded-lg shadow-inner' : 'text-slate-500 hover:text-white'}`}>
                  30D
                </button>
              </div>
            </div>
            <div className="h-[250px] md:h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#8B5CF6" />
                      <stop offset="100%" stopColor="#3B82F6" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.03)" />
                  <XAxis dataKey="name" hide />
                  <YAxis hide domain={['auto', 'auto']} />
                  <RechartsTooltip
                    contentStyle={{
                      background: 'rgba(0, 0, 0, 0.95)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '16px',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
                    }}
                    itemStyle={{ fontSize: '12px', fontWeight: 800, color: '#fff' }}
                    labelStyle={{ display: 'none' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="url(#lineGradient)"
                    strokeWidth={4}
                    fillOpacity={1}
                    fill="url(#areaGradient)"
                    animationDuration={1500}
                    activeDot={{ r: 8, fill: '#3B82F6', stroke: '#fff', strokeWidth: 2, className: "animate-pulse" }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </TiltCard>

          {/* ALLOCATION BAR CHART */}
          <TiltCard className="bg-black rounded-[2.5rem] p-8 border border-purple-500/30 shadow-[0_0_15px_rgba(168,85,247,0.05)] transition-all duration-500 flex flex-col h-full">
            <h3 className="text-white text-base font-black uppercase tracking-tight mb-8">Asset Allocation</h3>
            <div className="h-[250px] md:h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={allocationData} layout="vertical" barSize={12}>
                  <XAxis type="number" hide />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={100}
                    tick={{ fontSize: 13, fontWeight: 800, fill: '#CBD5E1', textTransform: 'uppercase' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <RechartsTooltip
                    cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                    contentStyle={{ background: '#000', border: 'none', borderRadius: '8px', fontSize: '10px' }}
                  />
                  <Bar dataKey="value" radius={[0, 10, 10, 0]} animationDuration={1000}>
                    {allocationData.map((entry, index) => {
                      let color = '#3B82F6';
                      if (entry.name === 'Fixed Deposit') color = '#8B5CF6';
                      if (entry.name === 'Stocks') color = '#22D3EE';
                      return <Cell key={`cell-${index}`} fill={color} className="filter drop-shadow-[0_0_8px_rgba(59,130,246,0.3)]" />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Real Estate</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Fixed Deposit</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Stocks</span>
              </div>
            </div>
          </TiltCard>
        </div>

        {/* === LIQUID ASSETS SECTION === */}
        {liquidAssets.length > 0 && (
          <TiltCard className="bg-black rounded-[2.5rem] overflow-hidden border border-purple-500/30 mb-8 shadow-[0_0_15px_rgba(168,85,247,0.05)] transition-all duration-500">
            <div className="p-8 bg-black border-b border-white/5 flex justify-between items-center backdrop-blur-md">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-2xl font-black text-white uppercase tracking-tight">Liquid Assets</h3>
                  <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 text-[10px] font-black rounded-full flex items-center gap-1.5 border border-emerald-500/20">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                    SYNCED TERMINAL
                  </span>
                </div>
                <p className="text-[12px] text-slate-400 font-bold uppercase tracking-widest mt-2">Equities • Mutual Funds • Digital Assets</p>
              </div>
              <div className="md:hidden text-slate-600 text-[8px] font-bold uppercase border border-white/5 px-2 py-1 rounded">Swipe to view →</div>
            </div>
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5 bg-black">
                    <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Asset Matrix</th>
                    <th className="px-8 py-6 text-right text-[12px] font-black text-slate-400 uppercase tracking-[0.2em]">Avg Cost</th>
                    <th className="px-8 py-6 text-right text-[12px] font-black text-slate-400 uppercase tracking-[0.2em]">Current Nav</th>
                    <th className="px-8 py-6 text-right text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Units</th>
                    <th className="px-8 py-6 text-right text-[12px] font-black text-slate-400 uppercase tracking-[0.2em]">Market Valuation</th>
                    <th className="px-8 py-6 text-center text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Control</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {liquidAssets.map((asset) => (
                    <tr key={asset.id} className="group hover:bg-white/[0.04] transition-all duration-300 cursor-default">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-lg font-black shadow-2xl transform group-hover:scale-110 transition-transform duration-500
                                            ${asset.assetType === 'Stocks' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                              asset.assetType === 'Crypto' ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20' : 'bg-purple-500/10 text-purple-400 border border-purple-500/20'}`}>
                            {asset.assetType === 'Stocks' ? <TrendingUp size={20} /> :
                              asset.assetType === 'Crypto' ? <Coins size={20} /> : <Briefcase size={20} />}
                          </div>
                          <div>
                            <div className="text-white font-bold text-base mb-1">{asset.assetName}</div>
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest bg-white/5 px-2 py-0.5 rounded border border-white/5">{asset.assetType}</span>
                              {asset.ticker && <span className="text-[11px] font-mono text-slate-400 tracking-tighter">{asset.ticker}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="text-slate-400 font-bold text-[12px] tracking-tight">₹{asset.buyPrice?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="text-white font-black text-base drop-shadow-sm">₹{asset.currentPrice?.toLocaleString('en-IN', { minimumFractionDigits: 1 })}</div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="text-white font-bold text-[12px]">{asset.quantity} {asset.assetType === 'Crypto' ? 'COINS' : 'UNITS'}</div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="text-white font-black text-base mb-0.5 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                          ₹{asset.currentValue?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </div>
                        <div className={`flex items-center justify-end gap-0.5 text-[11px] font-black tracking-tighter ${(asset.profitLoss || 0) >= 0 ? 'text-emerald-400' : 'text-rose-500'}`}>
                          {(asset.profitLoss || 0) >= 0 ? <ArrowUpRight size={8} /> : <ArrowDownRight size={8} />}
                          {(asset.profitLoss || 0) >= 0 ? '+' : ''}₹{Math.abs(asset.profitLoss || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button onClick={() => handleRemoveAsset(asset.id)}
                          className="p-2.5 text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 rounded-xl transition-all duration-300 opacity-0 group-hover:opacity-100">
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TiltCard>
        )}

        {/* === FIXED ASSETS SECTION === */}
        {fixedAssets.length > 0 && (
          <TiltCard className="bg-black rounded-[2.5rem] overflow-hidden border border-purple-500/30 mb-8 shadow-[0_0_15px_rgba(168,85,247,0.05)] hover:border-purple-500/60 transition-all duration-500">
            <div className="p-8 bg-black border-b border-white/5 backdrop-blur-md">
              <h3 className="text-2xl font-black text-white uppercase tracking-tight">Fixed Assets</h3>
              <p className="text-[12px] text-slate-400 font-bold uppercase tracking-widest mt-2">Immovable Assets • Fixed Income • Long-term Holdings</p>
            </div>
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5 bg-black">
                    <th className="px-8 py-6 text-[12px] font-black text-slate-400 uppercase tracking-[0.2em]">Asset Matrix</th>
                    <th className="px-8 py-6 text-right text-[12px] font-black text-slate-400 uppercase tracking-[0.2em]">Interest / Yield</th>
                    <th className="px-8 py-6 text-right text-[12px] font-black text-slate-400 uppercase tracking-[0.2em]">Principal</th>
                    <th className="px-8 py-6 text-right text-[12px] font-black text-slate-400 uppercase tracking-[0.2em]">Units</th>
                    <th className="px-8 py-6 text-right text-[12px] font-black text-slate-400 uppercase tracking-[0.2em]">Terminal Value</th>
                    <th className="px-8 py-6 text-center text-[12px] font-black text-slate-400 uppercase tracking-[0.2em]">Control</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {fixedAssets.map((asset) => {
                    const isMatured = asset.maturityDate && new Date(asset.maturityDate) < new Date();
                    return (
                      <tr key={asset.id} className="group hover:bg-white/[0.03] transition-all duration-300">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-lg font-black shadow-2xl transform group-hover:scale-110 transition-transform duration-500
                                              ${asset.assetType === 'Fixed Deposit' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                                asset.assetType === 'Real Estate' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                                  asset.assetType === 'Land' ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20' : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'}`}>
                              {asset.assetType === 'Real Estate' ? <Building2 size={20} /> :
                                asset.assetType === 'Fixed Deposit' ? <Landmark size={20} /> :
                                  asset.assetType === 'Land' ? <Home size={20} /> : <FileText size={20} />}
                            </div>
                            <div>
                              <div className="text-white font-bold text-base mb-1">{asset.assetName}</div>
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest bg-white/5 px-2 py-0.5 rounded border border-white/5">{asset.assetType}</span>
                                {isMatured && (
                                  <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">Matured</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-white font-black text-base drop-shadow-sm">₹{asset.currentPrice?.toLocaleString('en-IN', { minimumFractionDigits: 0 })}</div>
                          {asset.interestRate && <div className="text-[11px] font-black text-emerald-400 uppercase mt-1 tracking-widest">{asset.interestRate}% APY</div>}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-slate-400 font-bold text-[12px] tracking-tight">₹{asset.buyPrice?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-slate-100 font-bold text-[12px]">{asset.quantity} {asset.assetType === 'Fixed Deposit' ? 'UNITS' : 'POS.'}</div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-white font-black text-base mb-1 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                            ₹{asset.currentValue?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </div>
                          <div className={`text-[11px] font-black tracking-widest uppercase ${(asset.profitLoss || 0) >= 0 ? 'text-emerald-400' : 'text-rose-500'}`}>
                            {(asset.profitLoss || 0) >= 0 ? 'Surplus' : 'Deficit'}: ₹{Math.abs(asset.profitLoss || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button onClick={() => handleRemoveAsset(asset.id)}
                            className="p-2.5 text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 rounded-xl transition-all duration-300 opacity-0 group-hover:opacity-100">
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TiltCard>
        )}

        {/* Empty State */}
        {assets.length === 0 && (
          <div className="glass-card rounded-[3rem] p-20 text-center border border-white/5">
            <div className="w-24 h-24 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-indigo-500/20 shadow-[0_0_50px_rgba(99,102,241,0.1)]">
              <Bot size={48} className="text-indigo-400 animate-pulse" />
            </div>
            <h3 className="text-3xl font-black text-white mb-4 uppercase tracking-tighter">Terminal Empty</h3>
            <p className="text-slate-400 mb-10 max-w-md mx-auto font-medium leading-relaxed">Your neural portfolio is currently offline. Initialize your wealth tracking by adding your first liquid or fixed asset.</p>
            <div className="flex gap-6 justify-center">
              <AddAssetButton
                onAdd={handleAddAsset}
                assetType="liquid"
                label="Initialize Liquid"
                icon={<Sparkles size={18} />}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 glow-blue"
              />
            </div>
          </div>
        )}
      </div >
    </DashboardLayout >
  );
};

/* === DUAL ADD ASSET BUTTONS === */
const AddAssetButton = ({ onAdd, assetType, label, icon, className }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Filter asset types based on button type
  const availableTypes = assetType === 'liquid'
    ? ['Stocks', 'Mutual Fund', 'Crypto']
    : ['Fixed Deposit', 'Real Estate', 'Land', 'Other'];

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`flex items-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-md hover:shadow-lg ${className}`}>
        {icon}
        <span>{label}</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-2xl animate-in fade-in duration-300">
          <div className="bg-black rounded-[2.5rem] w-full max-w-lg shadow-[0_0_80px_rgba(139,92,246,0.15)] overflow-hidden border border-purple-500/30 relative hover-3d transition-all duration-500 group/modal">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent pointer-events-none group-hover/modal:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-purple-500 to-transparent shadow-[0_0_15px_rgba(168,85,247,0.5)]"></div>
            <div className="px-8 py-6 border-b border-white/5 flex justify-between items-center bg-black relative z-10 backdrop-blur-md">
              <div>
                <h3 className="text-xl font-black text-white uppercase tracking-tight flex items-center gap-2 text-glow-purple">
                  {assetType === 'liquid' ? <Sparkles size={18} className="text-purple-400" /> : <Building2 size={18} className="text-purple-400" />}
                  {assetType === 'liquid' ? 'Liquid Entry' : 'Fixed Position'}
                </h3>
                <p className="text-[9px] text-slate-500 font-bold uppercase tracking-[0.2em] mt-1 opacity-70">
                  Terminal Protocol
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-slate-500 hover:text-white hover:bg-white/5 p-2 rounded-xl transition-all duration-300 hover:rotate-90 border border-transparent hover:border-purple-500/20"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
              <AddAssetForm
                onSubmit={(asset) => { onAdd(asset); setIsOpen(false); }}
                availableTypes={availableTypes}
                assetCategory={assetType}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const AddAssetForm = ({ onSubmit, availableTypes, assetCategory }) => {
  const [type, setType] = useState(availableTypes[0]); // Default to first available type

  // Initial State structure
  const initialFormState = {
    name: '', ticker: '', quantity: '', price: '', currentMarketPrice: '', date: '',
    interestRate: '', maturityDate: '', description: '', area: ''
  };
  const [formData, setFormData] = useState(initialFormState);
  const [fetchingPrice, setFetchingPrice] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Reset form when type changes
  useEffect(() => {
    setFormData(initialFormState);
    setSuggestions([]);
    setShowSuggestions(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Dynamic Placeholders
  const getPlaceholders = (t) => {
    switch (t) {
      case 'Stocks': return { name: 'e.g. Reliance Industries', ticker: 'e.g. RELIANCE.BSE' };
      case 'Mutual Fund': return { name: 'e.g. Axis Bluechip Fund', ticker: 'e.g. 120465 (Scheme Code)' };
      case 'Crypto': return { name: 'e.g. Bitcoin', ticker: 'e.g. bitcoin' };
      case 'Fixed Deposit': return { name: 'e.g. SBI Term Deposit', ticker: '' };
      case 'Real Estate': return { name: 'e.g. 3BHK Apartment in Bandra', ticker: '' };
      case 'Land': return { name: 'e.g. Agricultural Land in Pune', ticker: '' };
      default: return { name: 'e.g. Gold Jewelry', ticker: '' };
    }
  }
  const placeholders = getPlaceholders(type);

  const fetchCurrentPrice = async (tickerVal) => {
    // If called via onClick, tickerVal is an Event object. We only want it if it's a string.
    const symbol = (typeof tickerVal === 'string' ? tickerVal : null) || formData.ticker;
    if (!symbol) return toast.error("Enter ticker first");

    setFetchingPrice(true);
    try {
      let price = null;
      if (type === 'Stocks') {
        const data = await financialDataAPI.getStockPrice(symbol);
        if (data) price = data.price;
      } else if (type === 'Mutual Fund') {
        const data = await financialDataAPI.getMFNAV(symbol);
        if (data) price = data.price;
      } else if (type === 'Crypto') {
        const data = await financialDataAPI.getCryptoPrice(symbol);
        if (data) price = data.price;
      }

      if (price) {
        // Set both Buy Price (as a convenience) and hidden Current Price
        setFormData(prev => ({
          ...prev,
          price: price,
          currentMarketPrice: price
        }));
        toast.success(`Current Price: ₹${price}`);
      } else toast.error("Price fetch failed");
    } catch (e) { toast.error("Error fetching price"); }
    finally { setFetchingPrice(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const qty = parseFloat(formData.quantity) || 1;

    // User Entered Buy Price
    let buyPrice = parseFloat(formData.price) || 0;

    // FIX FOR FIXED DEPOSITS: If type is FD, 'quantity' field is actually the 'Principal Allocation'
    // We treat 'quantity' as the total investment (buyPrice) and set internal quantity to 1
    // OR just ensure buyPrice is not 0.
    let finalQty = qty;
    if (type === 'Fixed Deposit') {
      buyPrice = qty; // Principal is the buy price
      finalQty = 1;   // Treat as 1 massive unit of FD
    }

    // Use Fetched Current Price if available, otherwise assume Buy Price is Current (roughly)
    const currentPrice = parseFloat(formData.currentMarketPrice) || buyPrice;

    const newAsset = {
      assetType: type,
      assetName: formData.name,
      ticker: (type === 'Stocks' || type === 'Crypto' || type === 'Mutual Fund') ? formData.ticker : null,
      quantity: finalQty,
      buyPrice: buyPrice,
      buyDate: formData.date || new Date().toISOString().split('T')[0],
      currentPrice: currentPrice,
      totalInvested: finalQty * buyPrice,
      interestRate: type === 'Fixed Deposit' ? parseFloat(formData.interestRate) : null,
      maturityDate: type === 'Fixed Deposit' ? formData.maturityDate : null,
      description: type === 'Real Estate' || type === 'Other' ? formData.description : null,
    };
    onSubmit(newAsset);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div>
        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-4">Select Asset Matrix</label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {availableTypes.map(t => (
            <button key={t} type="button" onClick={() => setType(t)}
              className={`px-4 py-3 text-[10px] font-black rounded-xl border transition-all duration-300 uppercase tracking-widest
                                ${type === t ? 'bg-blue-600/10 text-blue-400 border-blue-500/50 glow-blue' : 'bg-black text-slate-500 border-white/5 hover:bg-white/5 hover:text-slate-300'}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2 space-y-2">
          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Asset Identity</label>
          <div className="relative group">
            <input required
              className="w-full bg-black border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 placeholder:text-slate-600 group-hover:border-white/20"
              placeholder={placeholders.name}
              value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
            <div className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-600">
              <Info size={16} />
            </div>
          </div>
        </div>

        {(type === 'Stocks' || type === 'Crypto' || type === 'Mutual Fund') && (
          <div className="md:col-span-2 space-y-2 relative">
            <div className="flex justify-between">
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{type === 'Mutual Fund' ? 'Scheme Integration Code' : 'Market Ticker'}</label>
              {type === 'Mutual Fund' && <a href="https://www.mfapi.in/" target="_blank" rel="noreferrer" className="text-[10px] text-blue-400 font-black hover:underline tracking-widest uppercase">MF-Directory</a>}
            </div>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <input
                  className="w-full bg-black border border-white/10 rounded-2xl px-5 py-4 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300"
                  placeholder={placeholders.ticker}
                  value={formData.ticker}
                  onChange={async (e) => {
                    const val = e.target.value;
                    setFormData({ ...formData, ticker: val });
                    if (type === 'Stocks' && val.length > 2) {
                      const results = await financialDataAPI.searchStocks(val);
                      setSuggestions(results);
                      setShowSuggestions(true);
                    } else {
                      setShowSuggestions(false);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <ul className="absolute z-50 left-0 right-0 mt-3 bg-black border border-white/10 rounded-[2rem] shadow-2xl max-h-60 overflow-y-auto custom-scrollbar p-3 backdrop-blur-xl">
                    {suggestions.map((s, idx) => (
                      <li key={idx}
                        className="px-5 py-3 hover:bg-white/5 cursor-pointer rounded-2xl border-b border-white/5 last:border-0 transition-all duration-300 group"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, ticker: s.symbol, name: s.name }));
                          setShowSuggestions(false);
                          fetchCurrentPrice(s.symbol);
                        }}>
                        <div className="font-black text-white group-hover:text-blue-400 transition-colors uppercase tracking-tight">{s.symbol}</div>
                        <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">{s.name} • {s.exchange}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button type="button" onClick={fetchCurrentPrice} disabled={fetchingPrice}
                className="px-6 bg-blue-500/10 border border-blue-500/20 rounded-2xl text-[10px] font-black text-blue-400 hover:bg-blue-500/20 transition-all duration-300 uppercase tracking-widest">
                {fetchingPrice ? <RefreshCw className="animate-spin" size={16} /> : 'Sync Price'}
              </button>
            </div>
          </div>
        )}

        {(type === 'Real Estate' || type === 'Land' || type === 'Other') && (
          <div className="md:col-span-2 space-y-2">
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
              Position Specs
            </label>
            <textarea className="w-full bg-black border border-white/10 rounded-2xl px-5 py-4 text-sm text-white h-24 resize-none transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              placeholder={type === 'Land' ? 'e.g. 5 acres near Highway, Survey No. 123...' : 'Asset specifics and legal standing...'}
              value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
          </div>
        )}

        <div className="space-y-2">
          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{type === 'Fixed Deposit' ? 'Principal Allocation' : 'Unit Quantity'}</label>
          <input required type="number" step="any" className="w-full bg-black border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300"
            value={formData.quantity} onChange={e => setFormData({ ...formData, quantity: e.target.value })} />
        </div>

        <div className="space-y-2">
          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{type === 'Fixed Deposit' ? 'Starting FD Date' : 'Entry Valuation'}</label>
          {type === 'Fixed Deposit' ? (
            <input required type="date" className="w-full bg-black border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300 [color-scheme:dark]"
              value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
          ) : (
            <input required type="number" step="any" className="w-full bg-black border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300"
              value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} />
          )}
        </div>

        {type === 'Fixed Deposit' && (
          <>
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Interest Rate (%)</label>
              <input required type="number" step="0.01" className="w-full bg-black border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300"
                value={formData.interestRate} onChange={e => setFormData({ ...formData, interestRate: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Maturing Date</label>
              <input type="date" className="w-full bg-black border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300 [color-scheme:dark]"
                value={formData.maturityDate} onChange={e => setFormData({ ...formData, maturityDate: e.target.value })} />
            </div>
          </>
        )}
        {type !== 'Fixed Deposit' && (
          <div className="md:col-span-2 space-y-2">
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Transaction Date</label>
            <input required type="date" className="w-full bg-black border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300 [color-scheme:dark]"
              value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
          </div>
        )}
      </div>

      <button type="submit"
        className="relative group w-full bg-gradient-to-r from-purple-700 via-purple-600 to-indigo-600 text-white font-black py-5 rounded-[1.5rem] mt-8 text-xs uppercase tracking-[0.3em] transition-all duration-500 hover:shadow-[0_0_50px_rgba(139,92,246,0.5)] overflow-hidden border-t border-white/10">
        <span className="relative z-10 flex items-center justify-center gap-3">
          <CheckCircle2 size={18} className="text-white" />
          Finalize Terminal Entry
        </span>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
      </button>
    </form>
  );
};

export default Portfolio;